import { useCallback, useEffect, useState } from "react";
import { errorText } from "../../api/client";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { tapFeedback } from "../feedback";
import { CHART_RANGES, ScoreChart } from "../components/ScoreChart";
import type { ChartWeeks } from "../components/ScoreChart";
import { DayChart } from "../components/DayChart";
import type { DaySeries } from "../components/DayChart";
import { MonthCalendar } from "../components/MonthCalendar";
import { DaySheet } from "../components/DaySheet";
import type { DaySheetEntry } from "../components/DaySheet";
import { KidAvatar } from "../components/KidAvatar";
import { StarIcon } from "../components/StarIcon";
import { Emoji } from "../components/Emoji";
import type { History } from "../../shared/history";
import { addDays, dayKey, dayRange } from "../../shared/time";
import { canSeeTaps } from "../../shared/roles";
import type { PointEvent } from "../../shared/types";

type View = "calendar" | "graph";

/** How far back the graph reaches: today alone, tap by tap, or a number of weeks, day by day. */
type Span = "day" | ChartWeeks;

const VIEW_KEY = "pointz.diaryView";

/** The view this phone last chose. Blocked storage just means starting on the calendar. */
function savedView(): View {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "graph" ? "graph" : "calendar";
  } catch {
    return "calendar";
  }
}

function saveView(view: View): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* remembered for this visit only */
  }
}

/** Fetched days, tagged with the range they cover so another range's reply never shows. */
type Loaded = { range: string; data: Record<string, History> };

/**
 * Reads every kid's days from `from` to `to`, one request per kid in parallel, when the range
 * changes or `changes` does: a fingerprint of what moves a history (scores, stars, today's
 * close, who the kids are), not the whole snapshot, which is a new object every 30 seconds.
 * Every kid is read, not only the chosen ones, so switching a kid back on shows their line at
 * once. A kid whose read failed is left out rather than blanking the rest, and a superseded
 * read is aborted so stepping 4w, 12w, 52w does not stack requests.
 */
function useHistories(kidIds: string[], from: string, to: string, enabled: boolean, changes: string) {
  const { loadHistory } = useStore();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [err, setErr] = useState<{ range: string; text: string } | null>(null);
  const ids = kidIds.join(",");
  // Keyed on the range alone: a kid added or removed on another phone keeps what is on screen
  // (and an open day sheet) while the new read is in flight.
  const range = `${from}|${to}`;

  useEffect(() => {
    if (!enabled || !ids) return;
    const abort = new AbortController();
    const wanted = ids.split(",");
    void Promise.allSettled(wanted.map((id) => loadHistory(id, from, to, abort.signal))).then((results) => {
      if (abort.signal.aborted) return;
      const data: Record<string, History> = {};
      results.forEach((r, k) => {
        if (r.status === "fulfilled") data[wanted[k]] = r.value;
      });
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
      if (Object.keys(data).length > 0) setLoaded({ range, data });
      // Some kids missing says so plainly; nobody loaded gives the reason (offline, or the server's).
      const partly = Object.keys(data).length > 0;
      const text = partly ? "Couldn't load every kid's days." : errorText(failed?.reason, "Couldn't load the diary.");
      setErr(failed ? { range, text } : null);
    });
    return () => abort.abort();
  }, [ids, from, to, range, enabled, changes, loadHistory]);

  return {
    data: loaded?.range === range ? loaded.data : null,
    err: err?.range === range ? err.text : null,
  };
}

/** Today's taps for each kid, read when the graph shows today and again when any score moves. */
function useToday(ids: string[], today: string, enabled: boolean, changes: string) {
  const { loadDayEvents } = useStore();
  const [data, setData] = useState<Record<string, { opening: number; events: PointEvent[] }> | null>(null);
  const key = ids.join(",");

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void Promise.allSettled(key.split(",").filter(Boolean).map((id) => loadDayEvents(id, today))).then((results) => {
      if (!live) return;
      const next: Record<string, { opening: number; events: PointEvent[] }> = {};
      key.split(",").filter(Boolean).forEach((id, i) => {
        const r = results[i];
        if (r.status === "fulfilled") next[id] = r.value;
      });
      setData(next);
    });
    return () => {
      live = false;
    };
  }, [key, today, enabled, changes, loadDayEvents]);

  return data;
}

/**
 * The long-term view of every kid together: a month calendar with a mark per kid on each day,
 * or their scores as lines on one graph: today tap by tap, or 1, 4, 12 or 52 weeks day by day.
 * Kid chips choose who is shown (everyone at first, never no one). Every session sees every kid's scores, as on Rewards; taps
 * stay with grown-ups and the kid themselves.
 */
export function Diary({
  hidden,
  onHidden,
  startOnToday = false,
}: {
  hidden: string[];
  onHidden: (hidden: string[]) => void;
  /** Open on the graph at 1d, as when a kid's screen opens the Diary, instead of the last view. */
  startOnToday?: boolean;
}) {
  const { snapshot } = useStore();
  const today = dayKey(Date.now());
  const thisMonth = today.slice(0, 7);
  const [view, setView] = useState<View>(() => (startOnToday ? "graph" : savedView()));
  const [span, setSpan] = useState<Span>(startOnToday ? "day" : 12);
  const weeks: ChartWeeks = span === "day" ? 1 : span;
  const [month, setMonth] = useState(thisMonth);
  const [picked, setPicked] = useState<string | null>(null);

  const kids = snapshot?.kids ?? [];
  const ids = kids.map((k) => k.id);
  const chosenKids = kids.filter((k) => !hidden.includes(k.id));
  // A filter left over from a kid since removed could hide everyone: then show everyone.
  const shown = chosenKids.length > 0 ? chosenKids : kids;

  const chartFrom = addDays(today, -(weeks * 7 - 1));
  // What moves any kid's history; anything else in a fresh snapshot is no reason to re-read.
  const changes = kids.map((k) => `${k.id}:${k.score}:${k.stars}:${k.today.closed}:${k.today.star}`).join(",");
  const chart = useHistories(ids, chartFrom, today, view === "graph" && span !== "day", changes);
  // A kid's phone may read only their own taps, so on it today's graph shows just them.
  const dayKids = shown.filter((k) => snapshot !== null && canSeeTaps(snapshot.me, k.id));
  const todayTaps = useToday(
    dayKids.map((k) => k.id),
    today,
    view === "graph" && span === "day",
    changes,
  );
  // The server stops at today and starts at the day each kid was added, so the whole month is safe.
  const cal = useHistories(ids, `${month}-01`, lastOfMonth(month), view === "calendar", changes);

  const closeSheet = useCallback(() => setPicked(null), []);

  // Forget kids removed since (on any phone), so a stale id can never count towards the filter.
  const roster = ids.join(",");
  useEffect(() => {
    if (!snapshot) return;
    const kept = hidden.filter((id) => roster.split(",").includes(id));
    if (kept.length !== hidden.length) onHidden(kept);
  }, [snapshot, roster, hidden, onHidden]);

  function chooseView(next: View) {
    if (next === view) return;
    tapFeedback();
    setView(next);
    saveView(next);
  }

  // Worked out from who is shown, so the chips and the filter can never disagree.
  function toggle(kidId: string) {
    const on = shown.some((k) => k.id === kidId);
    // The last kid showing stays on: an empty diary would only look broken.
    if (on && shown.length === 1) return;
    tapFeedback();
    const off = kids.filter((k) => !shown.some((s) => s.id === k.id)).map((k) => k.id);
    onHidden(on ? [...off, kidId] : off.filter((id) => id !== kidId));
  }

  if (!snapshot) return null;
  if (kids.length === 0) {
    return (
      <main className="screen">
        <h1 className="title">
          <Emoji>📖</Emoji>Diary
        </h1>
        <p className="hint">The diary fills in once a kid has been added.</p>
      </main>
    );
  }

  const firstDay = shown.map((k) => dayKey(k.createdAt)).sort()[0];
  const sheetEntries: DaySheetEntry[] = picked
    ? shown.flatMap((kid) => {
        const day = cal.data?.[kid.id]?.days.find((d) => d.day === picked);
        return day ? [{ kid, day }] : [];
      })
    : [];

  return (
    <main className="screen diary">
      <h1 className="title">
        <Emoji>📖</Emoji>Diary
      </h1>

      <div className="segmented" role="group" aria-label="Show as">
        {(["calendar", "graph"] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={v === view}
            className="segmented__option"
            onClick={() => chooseView(v)}
          >
            <Emoji>{v === "calendar" ? "🗓️" : "📈"}</Emoji>
            {v === "calendar" ? "Calendar" : "Graph"}
          </button>
        ))}
      </div>

      {kids.length > 1 && (
        <div className="kid-filter" role="group" aria-label="Kids shown">
          {kids.map((k) => {
            const on = shown.some((s) => s.id === k.id);
            return (
              <button
                key={k.id}
                type="button"
                className="kid-chip"
                aria-pressed={on}
                style={kidStyle(k.colour)}
                onClick={() => toggle(k.id)}
              >
                <KidAvatar kid={k} size="sm" />
                <span className="kid-chip__name">{k.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {view === "graph" ? (
        <section className="section" aria-labelledby="diary-score">
          <div className="section__row">
            <h2 className="section__title" id="diary-score">
              Score
            </h2>
            <div className="segmented segmented--small" role="group" aria-label="How far back">
              {(["day", ...CHART_RANGES] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  aria-pressed={w === span}
                  aria-label={w === "day" ? "Today" : w === 1 ? "1 week" : `${w} weeks`}
                  className="segmented__option"
                  onClick={() => {
                    tapFeedback();
                    setSpan(w);
                  }}
                >
                  {w === "day" ? "1d" : `${w}w`}
                </button>
              ))}
            </div>
          </div>
          {span === "day" ? (
            <>
              {dayKids.length < shown.length && (
                <p className="hint">Today's graph shows only your own taps. The longer graphs show everyone.</p>
              )}
              {todayTaps ? (
                <DayChart
                  key="day"
                  series={dayKids
                    .filter((kid) => todayTaps[kid.id])
                    .map(
                      (kid): DaySeries => ({
                        kid,
                        shape: kids.indexOf(kid),
                        opening: todayTaps[kid.id].opening,
                        taps: todayTaps[kid.id].events.map((e) => ({ at: e.at, score: e.scoreAfter })),
                      }),
                    )}
                  start={dayRange(today).from}
                  now={Date.now()}
                />
              ) : (
                <p className="hint chart__loading">Loading…</p>
              )}
            </>
          ) : chart.err ? (
            <p className="error">{chart.err}</p>
          ) : null}
          {span === "day" ? null : chart.data ? (
            <ScoreChart
              key={weeks}
              series={shown.map((kid) => ({ kid, shape: kids.indexOf(kid), days: chart.data?.[kid.id]?.days ?? [] }))}
              from={chartFrom}
              today={today}
              weeks={weeks}
            />
          ) : (
            !chart.err && <p className="hint chart__loading">Loading…</p>
          )}
        </section>
      ) : (
        <section className="section" aria-label="Day by day">
          {cal.err && <p className="error">{cal.err}</p>}
          <MonthCalendar
            month={month}
            kids={shown.map((k) => ({ id: k.id, name: k.name, colour: k.colour, days: cal.data?.[k.id]?.days ?? null }))}
            today={today}
            firstDay={firstDay}
            onMonth={(m) => {
              tapFeedback();
              setMonth(m);
            }}
            onPick={(d) => {
              tapFeedback();
              setPicked(d);
            }}
          />
          <CalendarKey />
        </section>
      )}

      {picked && sheetEntries.length > 0 && <DaySheet date={picked} entries={sheetEntries} onClose={closeSheet} />}
    </main>
  );
}

/** What the marks under each date mean, in neutral ink so it reads for every kid. */
function CalendarKey() {
  return (
    <ul className="cal-key" aria-hidden="true">
      <li>
        <span className="cal__mark" data-state="star">
          <StarIcon className="cal__mark-star" />
        </span>
        Star day
      </li>
      <li>
        <span className="cal__mark" data-state="closed" />
        Closed
      </li>
      <li>
        <span className="cal__mark" data-state="open" />
        Open
      </li>
    </ul>
  );
}

/** The last day of a YYYY-MM month. */
function lastOfMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}
